import { useEffect, useRef, type ReactNode } from 'react';
export function MediaDialog({
  children,
  onDismiss,
  label,
  className = 'media-dialog',
}: {
  readonly children: ReactNode;
  readonly onDismiss: () => void;
  readonly label: string;
  readonly className?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  }, [onDismiss]);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const d = dialog.current;
    d?.showModal();
    d?.querySelector<HTMLElement>('button,input,select,textarea,a')?.focus();
    return () => {
      d?.close();
      previous?.focus();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      aria-label={label}
      className={className}
      onKeyDown={(e) => {
        if (e.key !== 'Tab') return;
        const nodes = [
          ...e.currentTarget.querySelectorAll<HTMLElement>(
            'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex]:not([tabindex="-1"])',
          ),
        ].filter((n) => n.getClientRects().length > 0);
        const first = nodes[0],
          last = nodes.at(-1);
        if (!first) {
          e.preventDefault();
          return;
        }
        if (
          e.shiftKey &&
          (document.activeElement === first ||
            !nodes.includes(document.activeElement as HTMLElement))
        ) {
          e.preventDefault();
          last?.focus();
        } else if (
          !e.shiftKey &&
          (document.activeElement === last ||
            !nodes.includes(document.activeElement as HTMLElement))
        ) {
          e.preventDefault();
          first.focus();
        }
      }}
      onCancel={(e) => {
        e.preventDefault();
        dismiss.current();
      }}
    >
      {children}
    </dialog>
  );
}
